import React, {useState} from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Collapse,
  IconButton,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import LanguageIcon from '@mui/icons-material/Language';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import {useNavigate} from 'react-router-dom';
import {useQuery, useMutation} from '@apollo/client';
import {GET_DOCUMENT_GROUPS, GET_GROUP_SUGGESTIONS} from '../graphql/queries';
import {CREATE_DOCUMENT_GROUP, ACCEPT_GROUP_SUGGESTION, DISCARD_GROUP_SUGGESTION} from '../graphql/mutations';
import {useAuth} from '../contexts/AuthContext';
import {useOrg} from '../contexts/OrgContext';
import {useDocumentTitle} from '../hooks/useDocumentTitle';
import {colors} from '../theme/colors';

function sourceTypeIcon(type: string) {
  switch (type) {
    case 'pdf': return <PictureAsPdfIcon sx={{fontSize: 18, color: colors.red[600]}} />;
    case 'url':
    case 'html': return <LanguageIcon sx={{fontSize: 18, color: colors.blue[500]}} />;
    case 'image': return <ImageIcon sx={{fontSize: 18, color: colors.purple[500]}} />;
    default: return <DescriptionIcon sx={{fontSize: 18, color: colors.gray[500]}} />;
  }
}

function formatRelativeTime(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString('en-US', {month: 'short', day: 'numeric'});
}

function guideStatusPill(group: any) {
  const guide = group.linkedGuide;
  if (!guide) {
    return <Chip label="No guide" size="small" variant="outlined" sx={{height: 22, fontSize: 12}} />;
  }
  const isOutOfDate = guide.isOutOfDate;
  const isPublished = !!guide.publishedAt;

  if (isOutOfDate) {
    return (
      <Chip
        label="Out of date"
        size="small"
        sx={{
          height: 22,
          fontSize: 12,
          bgcolor: colors.amber[50],
          color: colors.amber[700],
          border: `1px solid ${colors.amber[200]}`,
        }}
      />
    );
  }
  if (isPublished) {
    return <Chip label="Published" size="small" color="success" variant="filled" sx={{height: 22, fontSize: 12}} />;
  }
  return <Chip label="Draft" size="small" color="default" variant="outlined" sx={{height: 22, fontSize: 12}} />;
}

export default function DocumentGroupsPage() {
  useDocumentTitle('Document Groups');

  const {user} = useAuth();
  const {activeOrg} = useOrg();
  const navigate = useNavigate();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDesc, setNewGroupDesc] = useState('');

  const {loading, error, data} = useQuery(GET_DOCUMENT_GROUPS, {
    variables: {orgId: activeOrg?.id},
    skip: !user || !activeOrg?.id,
  });

  const [suggestionsOpen, setSuggestionsOpen] = useState(true);

  const {data: suggestionsData, refetch: refetchSuggestions} = useQuery(GET_GROUP_SUGGESTIONS, {
    variables: {orgId: activeOrg?.id},
    skip: !user || !activeOrg?.id,
  });

  const pendingSuggestions = (suggestionsData?.groupSuggestions || []).filter(
    (s: any) => s.status === 'pending'
  );

  const [acceptSuggestion] = useMutation(ACCEPT_GROUP_SUGGESTION, {
    refetchQueries: [
      {query: GET_DOCUMENT_GROUPS, variables: {orgId: activeOrg?.id}},
      {query: GET_GROUP_SUGGESTIONS, variables: {orgId: activeOrg?.id}},
    ],
  });

  const [discardSuggestion] = useMutation(DISCARD_GROUP_SUGGESTION, {
    onCompleted: () => refetchSuggestions(),
  });

  const [createGroup, {loading: creating}] = useMutation(CREATE_DOCUMENT_GROUP, {
    refetchQueries: [{query: GET_DOCUMENT_GROUPS, variables: {orgId: activeOrg?.id}}],
  });

  const handleCreate = async () => {
    if (!newGroupName.trim() || !activeOrg?.id) return;
    try {
      const result = await createGroup({
        variables: {
          orgId: activeOrg.id,
          name: newGroupName.trim(),
          description: newGroupDesc.trim() || undefined,
          groupType: 'JOURNEY',
        },
      });
      setCreateDialogOpen(false);
      setNewGroupName('');
      setNewGroupDesc('');
      const newId = result.data?.createDocumentGroup?.id;
      if (newId) {
        navigate(`/org/groups/${newId}`);
      }
    } catch (err: any) {
      console.error('Failed to create group:', err);
    }
  };

  const groups = data?.documentGroups || [];
  const hasGroups = !loading && !error && groups.length > 0;
  const isEmpty = !loading && !error && groups.length === 0;

  return (
    <Box sx={{maxWidth: 1040, margin: '0 auto', padding: '24px 24px 64px'}}>
      {/* Header */}
      <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3}}>
        <Box>
          <Typography sx={{fontSize: 24, fontWeight: 650, color: colors.textPrimary}}>
            Document Groups
          </Typography>
          <Typography sx={{fontSize: 14, color: '#6B7280', mt: 0.5}}>
            Curate source documents, then generate guides from them
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          New group
        </Button>
      </Box>

      {/* Suggestions banner */}
      {pendingSuggestions.length > 0 && (
        <Box
          sx={{
            mb: 2,
            bgcolor: '#fff',
            border: '1px solid',
            borderColor: colors.amber[200],
            borderRadius: '12px',
            overflow: 'hidden',
          }}
        >
          <Box
            onClick={() => setSuggestionsOpen(o => !o)}
            sx={{
              px: 2,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              bgcolor: colors.amber[50],
              '&:hover': {bgcolor: colors.amber[100]},
            }}
          >
            <LightbulbIcon sx={{fontSize: 18, color: colors.amber[600]}} />
            <Typography sx={{fontSize: 14, fontWeight: 600, flex: 1}}>
              {pendingSuggestions.length} New Suggestion{pendingSuggestions.length !== 1 ? 's' : ''}
            </Typography>
            <ExpandMoreIcon
              sx={{
                fontSize: 20,
                color: colors.gray[500],
                transform: suggestionsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
              }}
            />
          </Box>
          <Collapse in={suggestionsOpen}>
            {pendingSuggestions.map((suggestion: any) => (
              <Box
                key={suggestion.id}
                sx={{
                  px: 2,
                  py: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  borderTop: '1px solid',
                  borderColor: colors.amber[100],
                }}
              >
                <Box sx={{flex: 1, minWidth: 0}}>
                  <Typography sx={{fontSize: 14, fontWeight: 600}}>
                    {suggestion.name}
                  </Typography>
                  <Typography sx={{fontSize: 12, color: '#6B7280'}}>
                    {suggestion.sourceItems?.length || 0} doc{(suggestion.sourceItems?.length || 0) !== 1 ? 's' : ''}
                    {suggestion.rationale ? ` — ${suggestion.rationale}` : ''}
                  </Typography>
                  {suggestion.sourceItems?.length > 0 && (
                    <Box sx={{mt: 0.75, display: 'flex', flexDirection: 'column', gap: 0.25}}>
                      {suggestion.sourceItems.map((item: any) => (
                        <Box key={item.id} sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
                          {sourceTypeIcon(item.type)}
                          <Typography sx={{fontSize: 13, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                            {item.title}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
                <IconButton
                  size="small"
                  onClick={() => acceptSuggestion({variables: {suggestionId: suggestion.id}})}
                  sx={{
                    bgcolor: colors.green[50],
                    color: colors.green[600],
                    '&:hover': {bgcolor: colors.green[100]},
                  }}
                  title="Accept"
                >
                  <CheckIcon sx={{fontSize: 18}} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => discardSuggestion({variables: {suggestionId: suggestion.id}})}
                  sx={{
                    bgcolor: colors.gray[50],
                    color: colors.gray[500],
                    '&:hover': {bgcolor: colors.gray[100]},
                  }}
                  title="Dismiss"
                >
                  <CloseIcon sx={{fontSize: 18}} />
                </IconButton>
              </Box>
            ))}
          </Collapse>
        </Box>
      )}

      {/* List card */}
      <Box
        sx={{
          bgcolor: '#fff',
          border: '1px solid #E5E7EB',
          borderRadius: '12px',
          boxShadow: '0 1px 2px rgba(0,0,0,.06)',
          overflow: 'hidden',
        }}
      >
        {/* Table header */}
        <Box
          sx={{
            px: 2,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            borderBottom: '1px solid #E5E7EB',
            bgcolor: '#FAFAFA',
          }}
        >
          {[
            {label: 'Group', flex: 1, width: undefined, align: 'left'},
            {label: 'Sources', flex: undefined, width: 80, align: 'center'},
            {label: 'Guide status', flex: undefined, width: 120, align: 'left'},
            {label: 'Last updated', flex: undefined, width: 120, align: 'left'},
          ].map(col => (
            <Typography
              key={col.label}
              sx={{
                flex: col.flex,
                width: col.width,
                fontSize: 12,
                fontWeight: 600,
                color: '#6B7280',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                textAlign: col.align as any,
              }}
            >
              {col.label}
            </Typography>
          ))}
        </Box>

        {/* Loading */}
        {loading && (
          <Box sx={{display: 'flex', justifyContent: 'center', py: 8}}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{m: 2}}>
            Error loading groups: {error.message}
          </Alert>
        )}

        {/* Group rows */}
        {hasGroups && (
          <Box sx={{display: 'flex', flexDirection: 'column'}}>
            {groups.map((group: any) => (
              <Box
                key={group.id}
                onClick={() => navigate(`/org/groups/${group.id}`)}
                sx={{
                  px: 2,
                  py: 2,
                  cursor: 'pointer',
                  borderBottom: 1,
                  borderColor: 'divider',
                  '&:last-child': {borderBottom: 'none'},
                  '&:hover': {bgcolor: 'action.hover'},
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <FolderIcon sx={{color: colors.blue[500], fontSize: 24}} />
                <Box sx={{flex: 1, minWidth: 0}}>
                  <Typography sx={{fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                    {group.name}
                  </Typography>
                  {group.description && (
                    <Typography sx={{fontSize: 13, color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                      {group.description}
                    </Typography>
                  )}
                </Box>
                <Typography sx={{width: 80, textAlign: 'center', fontSize: 13, color: '#6B7280'}}>
                  {group.members?.length || 0}
                </Typography>
                <Box sx={{width: 120}}>
                  {guideStatusPill(group)}
                </Box>
                <Typography sx={{width: 120, fontSize: 13, color: '#9CA3AF'}}>
                  {formatRelativeTime(group.updatedAt)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Empty state */}
        {isEmpty && (
          <Box sx={{py: 6, px: 3, textAlign: 'center'}}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '999px',
                bgcolor: colors.blue[50],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
              }}
            >
              <FolderIcon sx={{color: colors.blue[600], fontSize: 24}} />
            </Box>
            <Typography sx={{fontSize: 18, fontWeight: 600, mt: 2}}>
              Create your first document group
            </Typography>
            <Typography sx={{fontSize: 14, color: '#6B7280', maxWidth: 420, mx: 'auto', mt: 1, lineHeight: 1.5}}>
              Groups let you curate source documents before generating a guide.
              Organize your sources first, then generate with one click.
            </Typography>
            <Box sx={{mt: 3, display: 'flex', justifyContent: 'center', gap: 1.5}}>
              <Button variant="contained" onClick={() => setCreateDialogOpen(true)}>
                Create a group
              </Button>
            </Box>

            {/* How it works */}
            <Box
              sx={{
                mt: 4,
                bgcolor: '#F9FAFB',
                borderRadius: '8px',
                p: 2,
                display: 'flex',
                gap: 3,
                justifyContent: 'center',
              }}
            >
              {[
                {num: '1', title: 'Create group', desc: 'Name and describe the process'},
                {num: '2', title: 'Add sources', desc: 'PDFs, URLs, documents'},
                {num: '3', title: 'Generate guide', desc: 'AI creates a draft from your sources'},
              ].map(step => (
                <Box key={step.num} sx={{display: 'flex', alignItems: 'flex-start', gap: 1}}>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      bgcolor: colors.blue[600],
                      color: '#fff',
                      fontSize: 11,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      mt: 0.25,
                    }}
                  >
                    {step.num}
                  </Box>
                  <Box sx={{textAlign: 'left'}}>
                    <Typography sx={{fontSize: 13, fontWeight: 600}}>{step.title}</Typography>
                    <Typography sx={{fontSize: 12, color: '#6B7280'}}>{step.desc}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {/* Create group dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{fontWeight: 600}}>Create new group</DialogTitle>
        <DialogContent>
          <Typography sx={{fontSize: 14, color: '#6B7280', mb: 2}}>
            A document group is a collection of source documents used to generate a guide.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Group name"
            value={newGroupName}
            onChange={e => setNewGroupName(e.target.value)}
            placeholder="e.g. Student Enrollment Process"
            sx={{mb: 2}}
          />
          <TextField
            fullWidth
            label="Description (optional)"
            value={newGroupDesc}
            onChange={e => setNewGroupDesc(e.target.value)}
            placeholder="Brief description of what this group covers"
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)} sx={{textTransform: 'none'}}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!newGroupName.trim() || creating}
            sx={{textTransform: 'none'}}
          >
            {creating ? <CircularProgress size={14} sx={{mr: 0.5}} /> : null}
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
